/**
 * @file index.js
 * @description 등록된 콘텐츠의 기본값과 메타데이터를 조회합니다.
 * @used-in content/useContent.js
 */

import { COMPANY_CONTENT } from './company';

// [기술] 서버 코드를 import하지 않고 동일 규칙을 복제하며 RG-1b가 서버 원문과 대조합니다.
export const CONTENT_KEY_PATTERN = /^[A-Za-z0-9_-]{1,40}(\.[A-Za-z0-9_-]{1,40}){1,5}$/;

// [기술] 엔트리 동결만으로는 중첩 배열을 보호하지 못하므로 기본 목록도 따로 동결합니다.
for (const entry of Object.values(COMPANY_CONTENT)) {
  if (Array.isArray(entry.defaultItems)) Object.freeze(entry.defaultItems);
  Object.freeze(entry);
}
export const CONTENT_REGISTRY = Object.freeze({ ...COMPANY_CONTENT });
const warnedKeys = new Set();
const EMPTY_ITEMS = Object.freeze([]);

// [기능] 같은 용도와 키의 경고를 한 번만 출력합니다.
function warnOnce(key, message) {
  if (!warnedKeys.has(key)) {
    warnedKeys.add(key);
    console.warn(message);
  }
}

// [기능] 상속 프로퍼티를 제외하고 등록된 엔트리만 반환합니다.
export function getContentEntry(key) {
  return Object.prototype.hasOwnProperty.call(CONTENT_REGISTRY, key)
    ? CONTENT_REGISTRY[key]
    : undefined;
}

// [기능] 기본 문구를 반환하고 미등록 키는 빈 문자열과 키당 한 번의 경고로 처리합니다.
export function getDefaultText(key) {
  const entry = getContentEntry(key);
  // [비즈니스] 리스트의 빈 문자열 반환이 검사에서 항상 일치로 둔갑하지 않도록 오용을 알립니다.
  if (entry && entry.type === 'list') {
    warnOnce('text:' + key, '[content] 리스트 키는 getDefaultText로 조회할 수 없습니다: ' + key);
    return '';
  }
  if (entry) return entry.defaultText;
  // [비즈니스] 키 오타로 페이지 전체를 중단하지 않으며 반복 경고로 콘솔을 채우지 않습니다.
  warnOnce(key, '[content] 등록되지 않은 콘텐츠 키입니다: ' + key);
  return '';
}

// [기능] 리스트 기본값을 반환하며 그 외 키에는 경고와 공용 빈 배열을 반환합니다.
export function getDefaultItems(key) {
  const entry = getContentEntry(key);
  // [기술] 동결된 원본을 반환해 소비자가 매 렌더 같은 배열 참조를 받게 합니다.
  if (entry && entry.type === 'list') return entry.defaultItems;
  warnOnce('items:' + key, '[content] 목록 기본값을 조회할 수 없는 콘텐츠 키입니다: ' + key);
  return EMPTY_ITEMS;
}

// [기능] 검사 도구용 기본 문자열 목록을 반환하며 미등록 키에도 경고를 내지 않습니다.
export function getDefaultStrings(key) {
  const entry = getContentEntry(key);
  if (!entry) return EMPTY_ITEMS;
  return entry.type === 'list' ? entry.defaultItems : [entry.defaultText];
}

// [기능] 등록 키를 오름차순으로 정렬한 독립적인 배열로 반환합니다.
export function listContentKeys() {
  return Object.keys(CONTENT_REGISTRY).sort();
}
